/**
 * Offline write queue (NFR-03, FR-AUD-02): scan batches and other retry-safe writes are
 * stored in IndexedDB with their client UUID key and sent when the device is online.
 * The server treats the key as an idempotency key, so a batch sent twice (lost reply,
 * page reload mid-sync) is applied once. Generic: any endpoint + JSON payload.
 *
 *   await enqueue({ endpoint: EP.scan.auditCount(id), payload: batch, key: batch.batchKey, label: 'Vault count' });
 *   await syncQueue();          // also runs on 'online' and at start-up (startQueueAutoSync)
 *
 * Entries are kept until the server accepts them. A 4xx reply (other than 401/408/429)
 * marks the entry failed so a person can look at it — it is never silently dropped.
 */
import { ApiError, api, isNetworkError } from './api';

export interface QueueEntry {
  /** Client UUID — also the batchKey / idempotency key. */
  key: string;
  endpoint: string;
  method: 'POST' | 'PUT' | 'PATCH';
  payload: unknown;
  /** Human label for the sync panel, e.g. "Audit MNL-A12 · vault". */
  label?: string;
  /** Number of reads/lines, for the indicator. */
  size?: number;
  createdAt: number;
  attempts: number;
  status: 'pending' | 'failed';
  lastError?: string;
}

export type Sender = (e: QueueEntry) => Promise<unknown>;

const DB_NAME = 'eros-offline';
const STORE = 'queue';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  dbPromise ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'key' }).createIndex('createdAt', 'createdAt');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      dbPromise = null;
      reject(req.error ?? new Error('IndexedDB unavailable'));
    };
  });
  return dbPromise;
}

function tx<T>(mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = run(t.objectStore(STORE));
        t.oncomplete = () => resolve(req.result);
        t.onerror = () => reject(t.error);
        t.onabort = () => reject(t.error);
      }),
  );
}

// ---- change notification (top-bar indicator, sync panel) ----
type Listener = () => void;
const listeners = new Set<Listener>();
const notify = (): void => listeners.forEach((l) => l());
export function onQueueChange(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export async function enqueue(e: Omit<QueueEntry, 'createdAt' | 'attempts' | 'status' | 'method'> & { method?: QueueEntry['method'] }): Promise<QueueEntry> {
  // Same key again = same batch: keep the original createdAt/attempts, replace the payload.
  const existing = await getEntry(e.key);
  const entry: QueueEntry = {
    method: 'POST',
    ...e,
    createdAt: existing?.createdAt ?? Date.now(),
    attempts: existing?.attempts ?? 0,
    status: 'pending',
  };
  await tx('readwrite', (s) => s.put(entry));
  notify();
  return entry;
}

export const getEntry = (key: string): Promise<QueueEntry | undefined> => tx('readonly', (s) => s.get(key) as IDBRequest<QueueEntry | undefined>);

export async function listQueue(): Promise<QueueEntry[]> {
  const all = await tx('readonly', (s) => s.getAll() as IDBRequest<QueueEntry[]>);
  return all.sort((a, b) => a.createdAt - b.createdAt);
}

export async function removeEntry(key: string): Promise<void> {
  await tx('readwrite', (s) => s.delete(key));
  notify();
}

/** Puts a failed entry back in line (the user fixed the cause). */
export async function retryEntry(key: string): Promise<void> {
  const e = await getEntry(key);
  if (!e) return;
  await tx('readwrite', (s) => s.put({ ...e, status: 'pending', lastError: undefined }));
  notify();
}

export const defaultSender: Sender = (e) => api(e.endpoint, { method: e.method, body: e.payload, idempotencyKey: e.key });

/** A rejection that will not change by retrying (validation, permission, conflict). */
const isPermanent = (err: unknown): boolean =>
  err instanceof ApiError && err.status >= 400 && err.status < 500 && ![401, 408, 425, 429].includes(err.status);

export interface SyncResult {
  sent: number;
  failed: number;
  remaining: number;
  /** True when the network dropped mid-sync (we stop and wait for 'online'). */
  offline: boolean;
}

let syncing: Promise<SyncResult> | null = null;

/** Sends pending entries oldest first. Concurrent callers share one run. */
export function syncQueue(send: Sender = defaultSender): Promise<SyncResult> {
  syncing ??= (async () => {
    const result: SyncResult = { sent: 0, failed: 0, remaining: 0, offline: false };
    try {
      for (const e of await listQueue()) {
        if (e.status !== 'pending') continue;
        try {
          await send(e);
          await tx('readwrite', (s) => s.delete(e.key));
          result.sent++;
        } catch (err) {
          if (isNetworkError(err)) {
            result.offline = true;
            await tx('readwrite', (s) => s.put({ ...e, attempts: e.attempts + 1, lastError: 'Offline' }));
            break;
          }
          const permanent = isPermanent(err);
          await tx('readwrite', (s) =>
            s.put({ ...e, attempts: e.attempts + 1, status: permanent ? 'failed' : 'pending', lastError: err instanceof Error ? err.message : String(err) }),
          );
          if (permanent) result.failed++;
          else break; // server trouble (5xx, 401): try again later, keep the order
        }
      }
      result.remaining = (await listQueue()).length;
      return result;
    } finally {
      notify();
      queueMicrotask(() => (syncing = null));
    }
  })();
  return syncing;
}

/** Call once at start-up: syncs now, on reconnect, and every 60 s while anything waits. */
export function startQueueAutoSync(send: Sender = defaultSender): () => void {
  if (typeof indexedDB === 'undefined') return () => undefined;
  const run = (): void => {
    if (navigator.onLine) void syncQueue(send).catch(() => undefined);
  };
  window.addEventListener('online', run);
  const timer = window.setInterval(() => {
    void listQueue()
      .then((q) => (q.some((e) => e.status === 'pending') ? run() : undefined))
      .catch(() => undefined);
  }, 60_000);
  run();
  return () => {
    window.removeEventListener('online', run);
    window.clearInterval(timer);
  };
}

/** Test hook: forget the cached connection (fake-indexeddb resets between tests). */
export function __resetForTests(): void {
  dbPromise = null;
  syncing = null;
}
